import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators
} from "@angular/forms";
import { Observable, of } from "rxjs";
import { debounceTime, map, startWith, switchMap, tap } from "rxjs/operators";
import { LoadingDrivebService } from "@shared/components/loading-driveb/loading-driveb.service";
import { ClientDetailsDTO } from "@shared/models/client-details-dto";
import { ClientService } from "@core/services/client.service";
import { MatOptionSelectionChange } from "@angular/material/core";

@Component({
    selector: "app-driveb-client-auto-complete-sales",
    templateUrl: "./client-auto-complete-sales.component.html",
    styleUrls: ["./client-auto-complete-sales.component.scss"],
    standalone: false
})
export class ClientAutoCompleteSalesComponent implements OnInit {
  // Input properties
  @Input() page = "";
  @Input() requiredText = "";
  @Input()
  set required(value: boolean) {
    if (value && !this.disabled) {
      this.searchForm.controls.client.setValidators([Validators.required]);
    }
  }
  @Input()
  set disabled(value: boolean) {
    if (value) {
      this.searchForm.disable();
    } else {
      this.searchForm.enable();
    }
  }

  // Output property
  @Output() onChange: EventEmitter<ClientDetailsDTO> =
    new EventEmitter<ClientDetailsDTO>();

  // Other properties
  loading = false;
  client = new UntypedFormControl();
  searchForm = this.fb.group({
    client: this.client
  });
  clients$: Observable<ClientDetailsDTO | ClientDetailsDTO[]>;

  constructor(
    private clientService: ClientService,
    private fb: UntypedFormBuilder,
    private loadingDrivebService: LoadingDrivebService
  ) {}

  ngOnInit(): void {
    this.setupPageLocalStorage();
    this.setupClientObservable();
  }

  private setupPageLocalStorage(): void {
    if (this.page != "") {
      const key = this.getKeyForPage(this.page);
      const client = localStorage.getItem(key);
      if (client) {
        const tradingName = JSON.parse(client)["tradingName"];
        this.searchForm.controls.client.setValue(tradingName);
      }
    } else {
      this.clean();
    }
  }

  private getKeyForPage(page: string): string {
    const pageToKeyMap: { [key: string]: string } = {
      contracts: "contracts-client"
    };

    return pageToKeyMap[page] || "";
  }

  private setupClientObservable(): void {
    this.clients$ = this.client.valueChanges.pipe(
      debounceTime(200),
      tap(() => {
        this.loading = true;
        this.loadingDrivebService.setAutoMode(false);
      }),
      switchMap((value: string) => {
        if (value) {
          return this.clientService.findClientByTradingName(value);
        } else {
          this.onChange.emit({} as ClientDetailsDTO);
          return of([]) as Observable<ClientDetailsDTO[]>;
        }
      }),
      map((result) => {
        this.loading = false;
        this.loadingDrivebService.setAutoMode(true);
        return result;
      })
    );
  }

  handleChangeClient(
    event: MatOptionSelectionChange,
    client: ClientDetailsDTO
  ) {
    if (event.isUserInput) {
      this.onChange.emit(client);
    }
  }

  clean(): void {
    this.searchForm.reset();
  }

  setNameFromLocalStorage(localStorageKey: string): void {
    const client = localStorage.getItem(localStorageKey);
    if (client) {
      const tradingName = JSON.parse(client)["tradingName"];
      this.searchForm.controls.client.setValue(tradingName);
    }
  }
}
